<template>
    <div>
        <v-card elevation="1">
            <v-card-text class="pb-1">
                <div class="caption mb-4">
                    <p>
                        This form is submitted to the State of Colorado to indicate that you meet all of the requirements of the
                        Colorado Workers' Compensation Act for being considered an independent contractor.
                    </p>
                    <p>
                        You are not being asked to sign this form at this time, since it must be signed in the presence of
                        a notary; Elite will contact you later to arrange a signing time.
                    </p>

                    <v-form v-model="valid" ref="form">
                        <label class="v-label" style="font-size: 12px">Federal Employer Id # or SSN</label>
                        <div style="font-size: 6pt">
                            <v-otp-input v-model="fein" length="9" plain type="number" @change="feinChange">
                            </v-otp-input>
                            <v-card-actions>
                                <v-spacer />
                                <v-btn x-small text class="mr-n2 mt-n3" @click="fein = ''">Clear</v-btn>
                                <v-btn x-small text class="mr-n2 mt-n3" @click="useSSN">Use SSN</v-btn>
                            </v-card-actions>
                        </div>

                        <v-text-field v-model="title" label="Title" hint="For example, 'Owner' or 'President'" :rules="[rules.required]"
                            @input="formChange"></v-text-field>
                    </v-form>

                    <div class="mt-3 mb-2">
                        <v-icon>mdi-bank-check</v-icon>
                        <span class="ml-2 font-weight-black">Certification</span>
                    </div>
                    <div class="my-2" style="xfont-size: 0.45rem;">
                        <p>
                            The State of Colorado requires that independent contractors certify their agreement
                            to nine criteria established by the <b>Colorado Workers’ Compensation Act</b>. You can click
                            <span class="primary--text">View Document</span> and review page 2 to see how these confirmations
                            will be used.
                        </p>
                        <p>
                            Please read the following nine statements, then acknowledge whether you agree or disagree with
                            all nine criteria by selecting either <b>Agree</b> or <b>Disagree</b> below:
                        </p>
                        <ol>
                            <li>
                                Elite Logistics, Inc. DOES NOT require you to work exclusively for them - you are free to
                                accept courier assignments from other companies whenever you wish;
                            </li>
                            <li>
                                Elite Logistics, Inc. DOES NOT oversee the completion of driving assignments or provide
                                instruction as to how assignments should be performed;
                            </li>
                            <li>
                                Elite Logistics, Inc. offers work on a contract rate basis only, and DOES NOT pay you a
                                salary or hourly rate;
                            </li>
                            <li>
                                Elite Logistics, Inc. DOES NOT terminate any courier assignment during the contract period,
                                unless you violate the terms of the contract or fail to complete the contractual terms of
                                the assignment;
                            </li>
                            <li>
                                Elite Logistics, Inc. DOES NOT provide training;
                            </li>
                            <li>
                                Elite Logistics, Inc. DOES NOT provide tools or benefits;
                            </li>
                            <li>
                                Elite Logistics, Inc. DOES NOT dictate the time of performance of driving assignments, other
                                than specifying a completion schedule and a range of agreeable work hours;
                            </li>
                            <li>
                                Elite Logistics, Inc. DOES NOT pay you, as an individual, personally, but will only make payment
                                to your trade or business entity;
                            </li>
                            <li>
                                Elite Logistics, Inc. DOES NOT offer to combine business operations with your company in any way.
                            </li>
                        </ol>
                    </div>

                    <v-radio-group row v-model="affirm" @change="formChange" :rules="[rules.required]">
                        <v-radio :value="false">
                            <template v-slot:label>
                                <div>Disagree</div>
                            </template>
                        </v-radio>
                        <v-radio :value="true">
                            <template v-slot:label>
                                <div>Agree</div>
                            </template>
                        </v-radio>
                    </v-radio-group>

                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                elevation="4"
                                block
                                outlined
                                color="primary"
                                class="my-2"
                                @click.stop="print()"
                            >
                                VIEW DOCUMENT
                            </v-btn>
                        </template>
                        <span>
                            VIEW DOCUMENT
                        </span>
                    </v-tooltip>
                </div>
            </v-card-text>
        </v-card>

        <not-saved-warning
            :dirty="dirty"
            message="NOT SAVED - MAKE SURE SSN and TITLE are specified, and the AGREE BUTTON is selected, THEN CLICK NEXT"
        ></not-saved-warning>

        <v-divider class="my-3" />

        <v-btn text @click="back"> Back </v-btn>

        <v-btn text color="primary" @click="next" :disabled="!valid || !einValid">
            Next
        </v-btn>
    </div>
</template>

<script>
import complianceMixins from "../common/props"
import ServerFile from "@/plugins/ServerFile"
import DocDef from "./sources/icstatus.js"

export default {
    mixins: [complianceMixins],

    data() {
        return {
            step: "95c40f8ef7ed4208bc7b320034bc84df", // magic, never change!
            vals: null,
            valid: true,
            dirty: false,
            einValid: false,
            docdef: null,
            fein: "",
            title: "Owner",
            affirm: undefined,
            currentFile: null,
            objectId: null
        }
    },

    methods: {
        formChange() {
            this.dirty = true
        },

        feinChange() {
            this.dirty = true
            this.einValid = this.fein.length == 9
        },

        viewDoc() {
            if (this.fileHandler.isLoaded) {
                this.fileHandler.getUrlAsBlob().then((blob) => {
                    window.open(blob, "_blank")
                })
            }
        },

        back() {
            if (this.dirty) {
                this.$confirm(this.lostMessage, {
                    icon: "mdi-help",
                    title: "Confirm",
                    color: "primary",
                }).then((res) => {
                    if (res) {
                        this.$emit("back")
                    }
                })
            } else {
                this.$emit("back")
            }
        },

        async next() {
            const vm = this

            if (this.dirty) {
                const vals = this.getStoreValues(["user*", "company*"])
                const ssn = vals.userSSN ? vals.userSSN.substr(-4) : "xxxx"

                this.$emit("loading", true)

                const args = {
                    userName: this.store.userName,
                    companyName: vals.companyName,
                    fein: this.fein,
                    job: "Contract Hauling",
                    address: `${vals.userStreet}${vals.userUnit ? " " + vals.userUnit : ""}, ${vals.userCity}, ${vals.userState} ${vals.userZip}`,
                    phone: vals.userPhone,
                    title: vm.title,
                    ssn: ssn,
                    userInitials: vm.initialsToImg(vals.userName)
                }

                const dd = this.getDocDef().getDocStructure(args)
                const blob = await this.generatePdf(dd)
                const fn = this.store.vue.$uuid.v1().replace(/-/g, "") + ".pdf"
                const fh = new ServerFile({vue: this.store.vue})
                await fh.saveFileObject(blob, this.getCompliancePath(), fn)

                const rec = {
                    step: this.step,
                    user: this.vals.userId,
                    compdate: this.store.vue.$time.local().toMillis(),
                    values: {
                        file: fn,
                        fein: vm.fein,
                        title: vm.title,
                        affirm: vm.affirm
                    }
                }

                if (this.objectId) {
                    rec.objectId = this.objectId
                }

                this.saveStepData(this.store.vue.$db, rec)
                .then(async () => {
                    if (vm.currentFile) {
                        //
                        //  Delete the old file, but it is not the end of the world if it fails, there
                        //  is nothing we can do about it.
                        //
                        const args = { vue: vm, token: vm.vals.userToken, url: vm.currentFile }
                        const fileHandler = new ServerFile(args)

                        await fileHandler.deleteUrl().catch(error => {
                            console.log(`*****  error deleting old step file: ${error} *****`)
                        })
                    }

                    vm.dirty = false
                    vm.$emit("next")
                })
                .catch(error => {
                    vm.$emit("error", error)
                })
                .finally(() => {
                    vm.$emit("loading", false)
                })
            }
            else {
                this.$emit("next")
            }
        },

        skip() {
            if (this.dirty) {
                this.$confirm(this.lostMessage, {
                    icon: "mdi-help",
                    title: "Confirm",
                    color: "primary",
                }).then((res) => {
                    if (res) {
                        this.$emit("next")
                    }
                })
            } else {
                this.$emit("next")
            }
        },

        getDocDef() {
            if (!this.docdef) {
                this.docdef = new DocDef()
            }
            return this.docdef
        },

        initialsToImg(name) {
            let names = name.trim().split(" ")
            let init = ""
            if (names.length < 2) {
                init = "ERR"
            }
            else {
                init = names[0][0].toUpperCase() + names[names.length-1][0].toUpperCase()
            }
            return this.$textToImg(init, {font: 'cursive', color: '#00567d', size: 12, stroke: 1, bold: true, italic: true, background: "rgba(0, 0, 0, 0)", lineHeight: '13.333px', baseline: .1})
        },

        async print() {
            const vm = this
            const vals = this.getStoreValues(["user*", "company*"])
            const ssn = vals.userSSN ? vals.userSSN.substr(-4) : "xxxx" //  `${vals.userSSN.substr(0, 3)}-${vals.userSSN.substr(4, 2)}-${vals.userSSN.substr(5)}`

            this.$emit("loading", true)

            let args = {
                userName: vals.userName,
                companyName: vals.companyName,
                fein: vm.fein ? vm.fein : "not specified yet",
                title: vm.title ? vm.title : "not specified yet",
                address: `${vals.userStreet}${vals.userUnit ? " " + vals.userUnit : ""}, ${vals.userCity}, ${vals.userState} ${vals.userZip}`,
                phone: vals.userPhone,
                ssn: ssn,
                userInitials: this.initialsToImg(vals.userName)
            }

            const dd = this.getDocDef().getDocStructure(args)
            const blob = await this.generatePdf(dd)
            window.open(URL.createObjectURL(blob), "_blank")
            this.$emit("loading", false)
        },

        useSSN() {
            if (this.fein != this.vals.userSSN) {
                this.fein = this.vals.userSSN
                this.feinChange()
            }
        }
    },

    beforeDestroy() {
        if (this.fileHandler) {
            this.fileHandler.cleanup()
        }
    },

    created() {
        const vm = this

        this.vals = this.getStoreValues(["user*"])

        //
        //  Get our saved data.  Its just the file name, if any, and the date
        //  last signed, which we don't currently display.
        //
        this.$emit("loading", true)
        this.getStepData(this.store.vue.$db, this.step, this.vals.userId)
        .then(data => {
            if (data.length === 1) {
                vm.objectId = data[0].objectId
                vm.fein = data[0].values.fein
                vm.title = data[0].values.title
                vm.affirm = data[0].values.affirm
                vm.einValid = vm.fein.length == 9
                vm.currentFile = vm.getComplianceUrl(data[0].values.file)
            }
        })
        .catch(error => {
            vm.$emit("error", error)
        })
        .finally(() => {
            vm.$emit("loading", false)
        })
    },
}
</script>

<style scoped>
.status-choice {
    font-size: 0.75rem;
}
</style>
